import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Form, ProgressBar, Alert, Tab, Tabs, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { auth, db } from '../firebase';
import qaService from '../services/qaService';
import { exportAnalyticsReportToPDF, exportUserReportToPDF, exportAttendanceReportToPDF } from '../utils/pdfExport';
import DailyReportLog from '../components/DailyReportLog';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const ReportsAnalytics = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState({});
  const [selectedTab, setSelectedTab] = useState('learning');
  const [dateRange, setDateRange] = useState('month');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [progressReports, setProgressReports] = useState([]);
  const [showProgressReport, setShowProgressReport] = useState(false);
  const [selectedProgressReport, setSelectedProgressReport] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [sourceData, setSourceData] = useState({
    users: [],
    attendanceRecords: [],
    issues: [],
    documents: []
  });

  useEffect(() => {
    if (currentUser) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, dateRange, selectedCohort]);

  const loadReportData = async () => {
    const toDate = (value) => {
      if (!value) return null;
      if (typeof value.toDate === 'function') return value.toDate();
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getDateRangeWindow = () => {
      const endDate = new Date();
      const startDate = new Date(endDate);
      switch (dateRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case 'month':
        default:
          startDate.setMonth(endDate.getMonth() - 1);
          break;
      }
      return { startDate, endDate };
    };

    try {
      const [
        usersSnapshot,
        modulesSnapshot,
        enrollmentsSnapshot,
        attendanceSnapshot,
        issuesSnapshot,
        cohortsSnapshot,
        programsSnapshot,
        progressReportsSnapshot,
        documents
      ] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'modules')),
        getDocs(collection(db, 'enrollments')),
        getDocs(collection(db, 'attendance')),
        getDocs(collection(db, 'issues')),
        getDocs(collection(db, 'cohorts')),
        getDocs(collection(db, 'programs')),
        getDocs(collection(db, 'progressReports')),
        qaService.getAllDocuments().catch(() => [])
      ]);

      const users = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(isRealUser);
      const modules = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const enrollments = enrollmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: toDate(doc.data().createdAt),
        startedAt: toDate(doc.data().startedAt),
        completedAt: toDate(doc.data().completedAt),
        lastAccessedAt: toDate(doc.data().lastAccessedAt)
      }));
      const attendanceRecords = attendanceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: toDate(doc.data().date) || toDate(doc.data().sessionDate) || toDate(doc.data().createdAt)
      }));
      const issues = issuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: toDate(doc.data().createdAt)
      }));
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const programsData = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const progressReportsRaw = progressReportsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        reportGeneratedAt: toDate(doc.data().reportGeneratedAt),
        createdAt: toDate(doc.data().createdAt),
        sessionDate: toDate(doc.data().sessionDate) || toDate(doc.data().reportDate) || toDate(doc.data().reportGeneratedAt) || toDate(doc.data().createdAt),
        submittedAt: toDate(doc.data().reportGeneratedAt) || toDate(doc.data().createdAt)
      }));

      setCohorts(cohortsData);
      setPrograms(programsData);

      const learners = users.filter(user => user.role === 'learner');
      const cohortUsers = selectedCohort === 'all'
        ? users
        : users.filter(user => user.cohortId === selectedCohort);
      const cohortLearners = selectedCohort === 'all'
        ? learners
        : learners.filter(user => user.cohortId === selectedCohort);
      const cohortLearnerIds = new Set(
        cohortLearners.flatMap(user => [user.id, user.uid].filter(Boolean))
      );

      const { startDate, endDate } = getDateRangeWindow();

      const filteredEnrollments = enrollments.filter(enrollment => {
        const enrollmentUserId = enrollment.userId || enrollment.learnerId || enrollment.uid;
        return cohortLearnerIds.has(enrollmentUserId);
      });

      const moduleCompletionRates = {};
      modules.forEach(module => {
        const moduleEnrollments = filteredEnrollments.filter(enrollment => enrollment.moduleId === module.id);
        const completedCount = moduleEnrollments.filter(enrollment => enrollment.completed === true).length;
        const enrolledCount = moduleEnrollments.length;

        moduleCompletionRates[module.id] = {
          name: module.title || module.moduleName || module.name || 'Module',
          enrolled: enrolledCount,
          completed: completedCount,
          rate: enrolledCount > 0 ? (completedCount / enrolledCount) * 100 : 0
        };
      });

      const totalEnrollments = filteredEnrollments.length;
      const totalCompletions = filteredEnrollments.filter(enrollment => enrollment.completed === true).length;

      const completionTimes = filteredEnrollments.map(enrollment => {
        const start = enrollment.startedAt || enrollment.createdAt;
        const end = enrollment.completedAt;
        if (!start || !end) return null;
        return (end - start) / (1000 * 60 * 60 * 24);
      }).filter(time => Number.isFinite(time));

      const mostPopularModule = Object.values(moduleCompletionRates).reduce((max, module) => {
        if (!max || module.enrolled > max.enrolled) return module;
        return max;
      }, null);

      const learnerPerformance = cohortLearners.map(learner => {
        const learnerEnrollments = filteredEnrollments.filter(enrollment => {
          const enrollmentUserId = enrollment.userId || enrollment.learnerId || enrollment.uid;
          return enrollmentUserId === learner.id || enrollmentUserId === learner.uid;
        });
        const completedCount = learnerEnrollments.filter(enrollment => enrollment.completed === true).length;
        const totalCount = learnerEnrollments.length;
        const completionRate = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
        const totalTime = learnerEnrollments.reduce((sum, enrollment) => {
          return sum + (enrollment.timeSpent || enrollment.totalTimeSpent || 0);
        }, 0);
        const lastActivity = learnerEnrollments.reduce((latest, enrollment) => {
          const activityDate = enrollment.lastAccessedAt || enrollment.completedAt || enrollment.createdAt;
          if (!activityDate) return latest;
          if (!latest || activityDate > latest) return activityDate;
          return latest;
        }, null);

        return {
          userId: learner.id,
          name: learner.displayName || learner.email || 'Unknown',
          completionRate,
          totalTime,
          enrolledModules: totalCount,
          lastActivity
        };
      });

      const performanceMetrics = {
        averageCompletionTime: completionTimes.length > 0
          ? completionTimes.reduce((sum, time) => sum + time, 0) / completionTimes.length
          : 0,
        mostPopularModule: mostPopularModule ? { name: mostPopularModule.name, enrollments: mostPopularModule.enrolled } : null,
        topPerformers: learnerPerformance
          .filter(learner => learner.completionRate > 0)
          .sort((a, b) => b.completionRate - a.completionRate)
          .slice(0, 5),
        strugglingLearners: learnerPerformance
          .filter(learner => learner.enrolledModules > 0 && learner.completionRate < 50)
          .sort((a, b) => a.completionRate - b.completionRate)
          .slice(0, 5)
      };

      const filteredAttendance = attendanceRecords.filter(record => {
        if (!record.date || Number.isNaN(record.date.getTime())) {
          return false;
        }
        const inRange = record.date >= startDate && record.date <= endDate;
        const cohortMatch = selectedCohort === 'all'
          ? true
          : record.cohortId === selectedCohort || cohortLearnerIds.has(record.learnerId);
        return inRange && cohortMatch;
      });

      const attendanceStats = filteredAttendance.reduce((stats, record) => {
        const status = (record.status || record.attendanceStatus || '').toLowerCase();
        stats.total += 1;
        if (status === 'present') stats.present += 1;
        if (status === 'absent') stats.absent += 1;
        if (status === 'excused') stats.excused += 1;
        if (status === 'late') stats.late += 1;
        return stats;
      }, {
        total: 0,
        present: 0,
        absent: 0,
        excused: 0,
        late: 0
      });
      attendanceStats.rate = attendanceStats.total > 0
        ? (attendanceStats.present / attendanceStats.total) * 100
        : 0;

      const usersById = users.reduce((map, user) => {
        map[user.id] = user;
        if (user.uid) {
          map[user.uid] = user;
        }
        return map;
      }, {});

      const modulesById = modules.reduce((map, module) => {
        map[module.id] = module;
        return map;
      }, {});

      const attendanceExportRows = filteredAttendance.map(record => {
        const user = usersById[record.learnerId] || {};
        const module = modulesById[record.moduleId] || {};
        return {
          learnerName: record.learnerName || user.displayName || user.email || 'Unknown',
          email: record.email || user.email || 'N/A',
          module: record.moduleName || module.title || module.moduleName || module.name || 'N/A',
          date: record.date,
          status: record.status || record.attendanceStatus || 'unknown'
        };
      });

      const filteredIssues = issues.filter(issue => {
        if (!issue.createdAt || Number.isNaN(issue.createdAt.getTime())) {
          return false;
        }
        const inRange = issue.createdAt >= startDate && issue.createdAt <= endDate;
        const cohortMatch = selectedCohort === 'all' ? true : issue.cohortId === selectedCohort;
        return inRange && cohortMatch;
      });

      const issueStats = {
        total: filteredIssues.length,
        open: filteredIssues.filter(issue => issue.status === 'open').length,
        inProgress: filteredIssues.filter(issue => issue.status === 'in_progress').length,
        resolved: filteredIssues.filter(issue => issue.status === 'resolved').length,
        highPriority: filteredIssues.filter(issue => issue.priority === 'high' && issue.status !== 'resolved').length
      };
      issueStats.resolutionRate = issueStats.total > 0
        ? (issueStats.resolved / issueStats.total) * 100
        : 0;

      const filteredDocuments = selectedCohort === 'all'
        ? documents
        : documents.filter(doc => doc.userInfo?.cohortId === selectedCohort);

      const documentStats = filteredDocuments.reduce((stats, doc) => {
        const status = (doc.status || doc.reviewStatus || doc.validationStatus || '').toLowerCase();
        stats.total += 1;
        if (status === 'approved' || status === 'verified' || status === 'valid') {
          stats.approved += 1;
        } else if (status === 'rejected' || status === 'invalid') {
          stats.rejected += 1;
        } else {
          stats.pending += 1;
        }
        return stats;
      }, {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
      });
      documentStats.complianceRate = documentStats.total > 0
        ? (documentStats.approved / documentStats.total) * 100
        : 0;

      const filteredProgressReports = progressReportsRaw
        .filter(report => {
          const cohortMatch = selectedCohort === 'all' ? true : report.cohortId === selectedCohort;
          if (!report.sessionDate) return cohortMatch;
          return cohortMatch && report.sessionDate >= startDate && report.sessionDate <= endDate;
        })
        .map(report => {
          const cohort = cohortsData.find(c => c.id === report.cohortId);
          const module = modulesById[report.moduleId] || {};
          const facilitator = usersById[report.facilitatorId] || {};
          const attendance = report.attendance || {};
          const attendanceCount = report.attendanceCount || attendance.attendanceCount || attendance.presentCount || 0;
          const totalStudents = report.totalStudents || attendance.totalStudents || attendance.totalCount || attendance.total || 0;
          const attendanceRate = report.attendanceRate || attendance.attendanceRate || (totalStudents > 0 ? Math.round((attendanceCount / totalStudents) * 100) : 0);
          const highlights = Array.isArray(report.highlights) ? report.highlights.join(' ') : report.highlights || '';
          const challenges = Array.isArray(report.challenges) ? report.challenges.join(' ') : report.challenges || '';

          return {
            ...report,
            cohortName: report.cohortName || cohort?.name || 'Unknown Cohort',
            moduleName: report.moduleName || module.title || module.moduleName || module.name || 'Unknown Module',
            facilitatorName: report.facilitatorName || facilitator.displayName || facilitator.email || 'Unknown Facilitator',
            attendanceCount,
            totalStudents,
            attendanceRate,
            highlights,
            challenges,
            feedback: report.feedback || report.studentFeedback || '',
            nextSessionNotes: report.nextSessionNotes || report.nextSessionPreparation?.notes || '',
            submittedAt: report.submittedAt || report.reportGeneratedAt || report.createdAt
          };
        })
        .sort((a, b) => {
          const aDate = a.sessionDate ? a.sessionDate.getTime() : 0;
          const bDate = b.sessionDate ? b.sessionDate.getTime() : 0;
          return bDate - aDate;
        });

      setProgressReports(filteredProgressReports);

      setSourceData({
        users: cohortLearners,
        attendanceRecords: attendanceExportRows,
        issues: filteredIssues,
        documents: filteredDocuments
      });

      setReportData({
        overview: {
          totalUsers: cohortUsers.length,
          totalLearners: cohortLearners.length,
          totalFacilitators: cohortUsers.filter(user => user.role === 'facilitator').length,
          totalModules: modules.length,
          totalEnrollments,
          totalCompletions,
          overallCompletionRate: totalEnrollments > 0 ? (totalCompletions / totalEnrollments) * 100 : 0
        },
        moduleCompletionRates,
        attendanceStats,
        documentStats,
        issueStats,
        performanceMetrics
      });
    } catch (error) {
      devError('Error loading report data:', error);
    }
  };

  const viewProgressReport = (report) => {
    setSelectedProgressReport(report);
    setShowProgressReport(true);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const exportReport = (reportType) => {
    const activeUsers = sourceData.users.filter(user => user.status === 'active').length;

    switch (reportType) {
      case 'comprehensive':
        exportAnalyticsReportToPDF({
          totalUsers: sourceData.users.length,
          activeUsers,
          completionRate: reportData.overview?.overallCompletionRate || 0,
          avgProgress: reportData.overview?.overallCompletionRate || 0,
          detailedData: Object.entries(reportData.moduleCompletionRates || {}).map(([id, data]) => ({
            metric: data.name,
            value: `${data.rate.toFixed(1)}%`,
            category: 'Module Completion',
            date: new Date().toISOString()
          }))
        }, 'Comprehensive Analytics Report');
        break;
      case 'module-completion':
        exportAnalyticsReportToPDF({
          totalUsers: sourceData.users.length,
          activeUsers,
          completionRate: reportData.overview?.overallCompletionRate || 0,
          detailedData: Object.entries(reportData.moduleCompletionRates || {}).map(([id, data]) => ({
            metric: data.name,
            value: `${data.rate.toFixed(1)}%`,
            category: 'Module Completion',
            date: new Date().toISOString()
          }))
        }, 'Module Completion Report');
        break;
      case 'attendance':
        exportAttendanceReportToPDF(sourceData.attendanceRecords, 'Attendance Report');
        break;
      case 'compliance':
        exportUserReportToPDF(sourceData.users, 'Compliance Report');
        break;
      default:
        devLog(`Unknown report type: ${reportType}`);
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (!currentUser || (userProfile?.role !== 'admin' && userProfile?.role !== 'facilitator')) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access reports and analytics.</p>
          <Button variant="primary" onClick={() => {
            if (currentUser?.role === 'facilitator') {
              navigate('/facilitator');
            } else {
              navigate('/dashboard');
            }
          }}>
            Go to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="reports" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Reports & Analytics</h1>
              <p className="mission-text mb-4">
                Data-driven insights for educational excellence
              </p>
              <p className="mb-0">
                Comprehensive analytics dashboard providing learning outcomes, attendance patterns, 
                progress tracking, and detailed session reports to optimize educational delivery.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2>Analytics Dashboard</h2>
                <p className="text-muted mb-0">Monitor learning outcomes and system performance</p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select 
                  size="sm" 
                  value={selectedCohort} 
                  onChange={(e) => setSelectedCohort(e.target.value)}
                  style={{ width: '200px' }}
                >
                  <option value="all">All Cohorts</option>
                  {cohorts.map(cohort => (
                    <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
                  ))}
                </Form.Select>
                <Form.Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </Form.Select>
                <Button variant="success" onClick={() => exportReport('comprehensive')}>
                  Export All
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Overview Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{reportData.overview?.totalLearners || 0}</h3>
                <small className="text-muted">Total Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{Math.round(reportData.overview?.overallCompletionRate || 0)}%</h3>
                <small className="text-muted">Completion Rate</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{Math.round(reportData.attendanceStats?.rate || 0)}%</h3>
                <small className="text-muted">Attendance Rate</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{Math.round(reportData.documentStats?.complianceRate || 0)}%</h3>
                <small className="text-muted">Compliance Rate</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Detailed Reports */}
        <Tabs activeKey={selectedTab} onSelect={setSelectedTab} id="reports-tabs" className="mb-4">
          <Tab eventKey="learning" title="Learning Analytics">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5>Module Completion Rates</h5>
                      <Button variant="outline-primary" size="sm" onClick={() => exportReport('module-completion')}>
                        Export
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {Object.values(reportData.moduleCompletionRates || {}).map(module => (
                      <div key={module.name} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span>{module.name}</span>
                          <span>{module.rate.toFixed(1)}% ({module.completed}/{module.enrolled})</span>
                        </div>
                        <ProgressBar 
                          now={module.rate} 
                          variant={module.rate >= 80 ? 'success' : module.rate >= 60 ? 'warning' : 'danger'}
                        />
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Performance Metrics</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Average Completion Time</strong></td>
                          <td>{Math.round(reportData.performanceMetrics?.averageCompletionTime || 0)} days</td>
                        </tr>
                        <tr>
                          <td><strong>Most Popular Module</strong></td>
                          <td>{reportData.performanceMetrics?.mostPopularModule?.name || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Total Enrollments</strong></td>
                          <td>{reportData.overview?.totalEnrollments || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Total Completions</strong></td>
                          <td>{reportData.overview?.totalCompletions || 0}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row className="mt-4">
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Top Performers</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Completion Rate</th>
                          <th>Study Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reportData.performanceMetrics?.topPerformers || []).map(learner => (
                          <tr key={learner.userId}>
                            <td>{learner.name}</td>
                            <td>
                              <Badge bg="success">{learner.completionRate.toFixed(1)}%</Badge>
                            </td>
                            <td>{formatTime(learner.totalTime)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Struggling Learners</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Completion Rate</th>
                          <th>Last Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reportData.performanceMetrics?.strugglingLearners || []).map(learner => (
                          <tr key={learner.userId}>
                            <td>{learner.name}</td>
                            <td>
                              <Badge bg="danger">{learner.completionRate.toFixed(1)}%</Badge>
                            </td>
                            <td>{learner.lastActivity ? learner.lastActivity.toLocaleDateString() : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="attendance" title="Attendance Reports">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5>Attendance Summary</h5>
                      <Button variant="outline-primary" size="sm" onClick={() => exportReport('attendance')}>
                        Export
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Table>
                      <tbody>
                        <tr>
                          <td><strong>Total Records</strong></td>
                          <td>{reportData.attendanceStats?.total || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Present</strong></td>
                          <td className="text-success">{reportData.attendanceStats?.present || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Absent</strong></td>
                          <td className="text-danger">{reportData.attendanceStats?.absent || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Excused</strong></td>
                          <td className="text-warning">{reportData.attendanceStats?.excused || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Attendance Rate</strong></td>
                          <td>
                            <Badge bg="success">{Math.round(reportData.attendanceStats?.rate || 0)}%</Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Attendance Visualization</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Present</span>
                        <span>{reportData.attendanceStats?.present || 0}</span>
                      </div>
                      <ProgressBar 
                        now={reportData.attendanceStats?.rate || 0} 
                        variant="success"
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Absent</span>
                        <span>{reportData.attendanceStats?.absent || 0}</span>
                      </div>
                      <ProgressBar 
                        now={reportData.attendanceStats?.total > 0 ? (reportData.attendanceStats.absent / reportData.attendanceStats.total) * 100 : 0} 
                        variant="danger"
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Excused</span>
                        <span>{reportData.attendanceStats?.excused || 0}</span>
                      </div>
                      <ProgressBar 
                        now={reportData.attendanceStats?.total > 0 ? (reportData.attendanceStats.excused / reportData.attendanceStats.total) * 100 : 0} 
                        variant="warning"
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="daily-reports" title="Daily Reports">
            <DailyReportLog />
          </Tab>

          <Tab eventKey="compliance" title="Compliance Reports">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5>Document Compliance</h5>
                      <Button variant="outline-primary" size="sm" onClick={() => exportReport('compliance')}>
                        Export
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Table>
                      <tbody>
                        <tr>
                          <td><strong>Total Documents</strong></td>
                          <td>{reportData.documentStats?.total || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Approved</strong></td>
                          <td className="text-success">{reportData.documentStats?.approved || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Pending</strong></td>
                          <td className="text-warning">{reportData.documentStats?.pending || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Rejected</strong></td>
                          <td className="text-danger">{reportData.documentStats?.rejected || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Compliance Rate</strong></td>
                          <td>
                            <Badge bg="success">{Math.round(reportData.documentStats?.complianceRate || 0)}%</Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5>Issue Resolution</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table>
                      <tbody>
                        <tr>
                          <td><strong>Total Issues</strong></td>
                          <td>{reportData.issueStats?.total || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Resolved</strong></td>
                          <td className="text-success">{reportData.issueStats?.resolved || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>In Progress</strong></td>
                          <td className="text-warning">{reportData.issueStats?.inProgress || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Open</strong></td>
                          <td className="text-danger">{reportData.issueStats?.open || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Resolution Rate</strong></td>
                          <td>
                            <Badge bg="success">{Math.round(reportData.issueStats?.resolutionRate || 0)}%</Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
          
          <Tab eventKey="progress-reports" title="Progress Reports">
            <Row>
              <Col md={12}>
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5>Facilitator Progress Reports</h5>
                      <small className="text-muted">Detailed session reports including attendance, highlights, challenges, and feedback</small>
                    </div>
                    <Badge bg="primary">{progressReports.length} Reports</Badge>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info">
                      <strong>📋 Progress reports include:</strong>
                      <ul className="mb-0 mt-2">
                        <li><strong>Attendance:</strong> Student participation rates and patterns</li>
                        <li><strong>Highlights:</strong> Successful learning outcomes and achievements</li>
                        <li><strong>Challenges:</strong> Difficulties encountered and areas needing improvement</li>
                        <li><strong>Feedback:</strong> Student input and suggestions for course enhancement</li>
                      </ul>
                    </Alert>
                    
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Cohort</th>
                          <th>Module</th>
                          <th>Facilitator</th>
                          <th>Attendance</th>
                          <th>Key Outcomes</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progressReports.map(report => {
                          return (
                            <tr key={report.id}>
                              <td>{report.sessionDate ? report.sessionDate.toLocaleDateString() : 'N/A'}</td>
                              <td>
                                <div>
                                  <strong>{report.cohortName}</strong>
                                  <br/>
                                  <small className="text-muted">
                                    {programs.find(p => p.id === cohorts.find(c => c.id === report.cohortId)?.programId)?.name}
                                  </small>
                                </div>
                              </td>
                              <td>{report.moduleName}</td>
                              <td>{report.facilitatorName}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{report.attendanceCount}/{report.totalStudents}</span>
                                  <Badge bg={report.attendanceRate >= 80 ? 'success' : report.attendanceRate >= 60 ? 'warning' : 'danger'}>
                                    {report.attendanceRate}%
                                  </Badge>
                                </div>
                              </td>
                              <td>
                                <div style={{ maxWidth: '200px' }}>
                                  <small className="text-muted">
                                    {report.highlights.length > 60 
                                      ? report.highlights.substring(0, 60) + '...' 
                                      : report.highlights
                                    }
                                  </small>
                                </div>
                              </td>
                              <td>
                                <Badge bg="success">Complete</Badge>
                              </td>
                              <td>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => viewProgressReport(report)}
                                >
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    
                    {progressReports.length === 0 && (
                      <div className="text-center p-4 text-muted">
                        <div className="display-4 mb-3">📋</div>
                        <h6>No progress reports available</h6>
                        <p>Progress reports will appear here once facilitators submit session feedback.</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Container>
      
      {/* Progress Report Detail Modal */}
      <Modal show={showProgressReport} onHide={() => setShowProgressReport(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>📋 Progress Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProgressReport && (
            <div>
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="border-primary">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0">Session Information</h6>
                    </Card.Header>
                    <Card.Body>
                      <p><strong>Date:</strong> {selectedProgressReport.sessionDate ? selectedProgressReport.sessionDate.toLocaleDateString() : 'N/A'}</p>
                      <p><strong>Cohort:</strong> {selectedProgressReport.cohortName}</p>
                      <p><strong>Module:</strong> {selectedProgressReport.moduleName}</p>
                      <p className="mb-0"><strong>Facilitator:</strong> {selectedProgressReport.facilitatorName}</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-success">
                    <Card.Header className="bg-success text-white">
                      <h6 className="mb-0">Attendance Summary</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <span className="me-3">Present: {selectedProgressReport.attendanceCount}/{selectedProgressReport.totalStudents}</span>
                        <Badge bg="success" style={{ fontSize: '1rem' }}>
                          {selectedProgressReport.attendanceRate}%
                        </Badge>
                      </div>
                      <ProgressBar 
                        now={selectedProgressReport.attendanceRate} 
                        variant="success" 
                        className="mb-2"
                      />
                      <p className="mb-0"><strong>Submitted:</strong> {selectedProgressReport.submittedAt ? selectedProgressReport.submittedAt.toLocaleString() : 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={12}>
                  <Card className="border-success">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">🌟 Session Highlights</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="bg-light p-3 rounded">
                        {selectedProgressReport.highlights}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="border-warning h-100">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">⚠️ Challenges & Issues</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="bg-light p-3 rounded">
                        {selectedProgressReport.challenges || 'No challenges reported'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-info h-100">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">💬 Student Feedback</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="bg-light p-3 rounded">
                        {selectedProgressReport.feedback || 'No feedback recorded'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Card className="border-secondary">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">📝 Notes for Next Session</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="bg-light p-3 rounded">
                        {selectedProgressReport.nextSessionNotes || 'No notes for next session'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProgressReport(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={() => exportReport('progress-report')}>
            Export Report
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReportsAnalytics;
